import humanizeString from 'humanize-string'
import isEmpty from 'lodash/isEmpty'

import {
  BookingTagIdInput,
  IDate,
  TemporalCondition,
} from '@avantstay/arriere-clients/dist/arriereBackoffice'
import {
  formatDate,
  formatDateRangeExtraOptions,
  getDatesFromPeriod,
} from '@avantstay/backoffice-core'
import { formatDateRange } from '@avantstay/format-date-range'

import { DateTypeProps } from '@new/__global/components/DatePickerCustomFilter/DatePickerCustomFilter.types'
import { getPeriodReferenceValue } from '@new/__global/components/DatePickerCustomFilter/DatePickerCustomFilter.utils'
import { BookingAcquisitionView } from '@new/__global/components/Filters/Filters.types'
import { RangeValuesLabelProps } from '@new/__global/components/Filters/FiltersVariations/Range'
import { BookingDatesRawData } from '@new/domains/bookings/screens/bookingsList/BookingsList.types'
import {
  BookingsFiltersProps,
  PaymentAuditQuickFilter,
  QuickFilters,
} from '@new/domains/bookings/screens/bookingsList/components/Filters/BookingsListFilter.types'

export const getIsDateRange = ({ period, periodReference }: BookingDatesRawData) => {
  const { fromDate, toDate } = getDatesFromPeriod(period)

  return !(periodReference || fromDate === toDate)
}

export const parsePeriodReference = (dateRawData: BookingDatesRawData) => {
  const { periodReference } = dateRawData

  const periodReferenceValue = getPeriodReferenceValue(periodReference)

  if (periodReferenceValue && periodReferenceValue !== DateTypeProps.customRange)
    return `${humanizeString(periodReferenceValue)}:`

  return getIsDateRange(dateRawData) ? 'Between:' : 'On:'
}

export const parseDatesByPeriodReference = (
  periodValue: IDate,
  periodReference?: TemporalCondition,
) => {
  const { fromDate, toDate } = getDatesFromPeriod(String(periodValue))

  if (periodReference) return formatDate(periodValue, 'MMM d, yy')

  if (fromDate === toDate) return formatDate(fromDate, 'MMM d, yy')

  return formatDateRange(fromDate, toDate, 'en-US', formatDateRangeExtraOptions)
}

export const getActiveFiltersLength = (filters: BookingsFiltersProps) =>
  Object.values(filters)
    .filter(option => !Object.values(TemporalCondition).includes(option))
    .filter(option => option !== BookingAcquisitionView.allBookings)
    .filter(item => !isEmpty(item) || !!item).length

export const getLabelWithPeriodReference = (dateRawData?: BookingDatesRawData) => {
  return dateRawData && dateRawData.period
    ? `${parsePeriodReference(dateRawData)} ${parseDatesByPeriodReference(
        dateRawData.period,
        dateRawData.periodReference,
      )}`
    : undefined
}

export const getLabelWithRangeValue = ({
  fromRangeValue,
  toRangeValue,
  formatValue,
}: RangeValuesLabelProps) => {
  const getFormattedValue = (value: string) => (formatValue ? formatValue(value) : value)
  const minPriceLabel = fromRangeValue ? `Min. ${getFormattedValue(fromRangeValue)}` : ''
  const maxPriceLabel = toRangeValue ? `Max. ${getFormattedValue(toRangeValue)}` : ''

  return `${minPriceLabel} ${maxPriceLabel}`
}

export const quickFilterOptions = [
  {
    label: 'All Bookings',
    value: QuickFilters.all,
  },
  {
    label: 'Pre-Stay Issues',
    value: QuickFilters.preStayIssues,
  },
  {
    label: 'Risk Management',
    value: QuickFilters.riskManagement,
  },
  {
    label: 'Payment Auditing',
    value: QuickFilters.paymentAuditing,
  },
  {
    label: 'Upcoming Arrivals',
    value: QuickFilters.upcomingArrivals,
  },
]

export const paymentAuditQuickFilterOptions = [
  {
    label: 'None',
    value: PaymentAuditQuickFilter.none,
  },
  {
    label: 'New Bookings',
    value: PaymentAuditQuickFilter.newBookings,
  },
  { label: 'Follow Ups', value: PaymentAuditQuickFilter.followUps },
  { label: 'Check-in 15 days', value: PaymentAuditQuickFilter.checkInFifteenDays },
  { label: 'ACH', value: PaymentAuditQuickFilter.ach },
  { label: 'Expedia VCC', value: PaymentAuditQuickFilter.expediaVcc },
  { label: 'In Progress', value: PaymentAuditQuickFilter.inProgress },
  { label: 'Owner Stays', value: PaymentAuditQuickFilter.ownerStays },
  { label: 'Wires', value: PaymentAuditQuickFilter.wires },
  { label: 'Deposit Refunds', value: PaymentAuditQuickFilter.depositRefunds },
  { label: 'Unbalanced Check-ins', value: PaymentAuditQuickFilter.unbalancedCheckIns },
]

export const parseTagsToRequest = (tags?: string[]) => {
  return tags?.map(
    tag =>
      ({ tagLevel: tag.split('-')[0], id: parseFloat(tag.split('-')[1]) }) as BookingTagIdInput,
  )
}
